import React, {useDebugValue, useMemo, useState} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks/useSearchFormContext';

import {
    hasFilledDatesValues,
    hasPartialDatesValues,
    getDatesDayDataIds,
    getDatesSelectDateHintDayData,
    shouldDatesFieldNotAutoProgressOnCancel,
    shouldDatesFieldAutoProgressOnClose,
    shouldDatesFieldAutoProgressOnSubmit,
    showDatesInputFieldError,
    showDatesInputIcon1,
    showDatesInputIcon2,
    getDatesPickerModalTitleMessage,
    getDateFormatOptions
} from '../../../utils/dates';
import {checkDatePickerSelectDateHintExperiment, runDatesAndNumberOfNightsExperiment, runConsistentDayOfWeekExperiment, runSearchFormDateFormatExperiment} from '../../../utils/experiments';

import {SearchIcon} from '../../SearchIcon/SearchIcon';
import {SearchDatesPickerModalTitle} from '../../SearchDatesPickerModalTitle/SearchDatesPickerModalTitle';
import {withSearchDayTemplate} from '../../SearchDayTemplate/withSearchDayTemplate';

const classNamespace = 'search-form';

const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const datesDetailsInputFieldClassName = `${classNamespace}__dates-details-input-field`;
const datesDetailsInputFieldWithNightsCountClassName = `${classNamespace}__dates-details-input-field-with-nights-count`;

const datesPickerClassName = `${classNamespace}__dates-picker`;
const datesPickerModalHeaderExtendedClassName = `${datesPickerClassName}--modal-header-extended`;

// eslint-disable-next-line complexity
function useComponent() {
    const {
        systemOptions: {
            locale
        },
        mediaSettings,
        mediaSettings: {
            isMobile
        },
        abacusStore,
        formOptions,
        destinationActions: {
            setIsTypeaheadInputFocus
        },
        datesActions: {
            setDayData: setDatesDayData,
            setDayDataIds: setDatesDayDataIds,
            setIsOpen: setDatesIsOpen,
            setIsSelecting: setDatesIsSelecting,
            setShowContinueButton,
            setCurrentDatesValuesDefault,
            setCurrentDatesValuesLatest,
            setLatestDatesValues,
            setAllDatesValuesDefault
        },
        datesEvents: {
            onCancel,
            onClose,
            onDateRangeChange,
            onDateRangeClear,
            onDisabledDayClick,
            onFocus,
            onOpen,
            onSelectionModeChange,
            onSubmit
        },
        datesOptions,
        datesOptions: {
            actionLocation,
            alertTemplateComponent,
            alertTemplateData,
            centered,
            consolidateInputs,
            dateFormatOptions: propDateFormatOptions,
            dayTemplateComponent: DayTemplate,
            disableTextInput,
            displayYearFirst,
            enableSameDayDateRange,
            fadeOut,
            fadeOutDuration,
            id,
            inputIcon1Name,
            inputIcon2Name,
            inputName1,
            inputName2,
            maxDate,
            minDate,
            parentNode,
            placement,
            positionIconRight,
            scrollNode,
            showArrow,
            splitInputs,
            wdioPrefix
        },
        datesSettings: {
            dayData,
            selectedDayDataId,
            isOpen,
            readOnly,
            showContinueButton
        },
        datesTranslations,
        datesTranslations: {
            startDateLabel,
            endDateLabel,
            startDateText,
            endDateText,
            directionsMessage,
            clearButtonLabel,
            closeButtonLabel,
            continueButtonLabel,
            calendarYearLabel,
            calendarMonthLabels,
            calendarShortMonthLabels,
            calendarDayLabels,
            calendarShortDayLabels,
            prevMonthButtonLabel,
            nextMonthButtonLabel,
            dateUnavailableMessage,
            datesClearedMessage,
            startDateClearedMessage,
            startDateInvalidMessage,
            startDateSelectedMessage,
            endDateClearedMessage,
            endDateInvalidMessage,
            endDateSelectedMessage
        },
        latestDatesValues,
        latestDatesValues: {
            startDate,
            endDate
        },
        guestsActions: {
            setIsOpen: setGuestsIsOpen
        }
    } = useSearchFormContext();

    const [isCancelCalled, setIsCancelCalled] = useState(false);

    const shouldShowDatesAndNightsSummary = runDatesAndNumberOfNightsExperiment({shouldLogExperiment: false}, abacusStore);

    let chooseClassNames = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showDatesInputFieldError(mediaSettings, formOptions, datesOptions, latestDatesValues)
    }, detailsFieldClassName, detailsInputFieldClassName, datesDetailsInputFieldClassName);

    if (shouldShowDatesAndNightsSummary) {
        chooseClassNames = classNames(fieldClassName, inputFieldClassName, {
            [inputFieldErrorClassName]: showDatesInputFieldError(mediaSettings, formOptions, datesOptions, latestDatesValues)
        }, detailsFieldClassName, datesDetailsInputFieldClassName, datesDetailsInputFieldWithNightsCountClassName);
    }

    const className = chooseClassNames;

    const pickerClassName = classNames(datesPickerClassName, {
        [datesPickerModalHeaderExtendedClassName]: isMobile && (checkDatePickerSelectDateHintExperiment(abacusStore))
    });

    const inputIcon1 = showDatesInputIcon1(formOptions, datesOptions) && (
        <SearchIcon name={inputIcon1Name}/>
    ) || null;

    const inputIcon2 = showDatesInputIcon2(formOptions, datesOptions) && (
        <SearchIcon name={inputIcon2Name}/>
    ) || null;

    const modalTitle = (
        (checkDatePickerSelectDateHintExperiment(abacusStore))
            ? <SearchDatesPickerModalTitle/>
            : getDatesPickerModalTitleMessage(datesTranslations, latestDatesValues)
    );

    const SearchDayTemplate = useMemo(() => (
        withSearchDayTemplate(DayTemplate, abacusStore, datesTranslations)
    ), /* eslint-disable-line react-hooks/exhaustive-deps */ [DayTemplate]); // FIXME: Add `abacusStore` and `datesTranslations` to deps

    const calendarLabels = {
        yearLabel: calendarYearLabel,
        monthNames: calendarMonthLabels,
        shortMonthNames: calendarShortMonthLabels,
        dayNames: calendarDayLabels,
        shortDayNames: calendarShortDayLabels
    };

    const i18n = {
        ariaLabelPrevButton: prevMonthButtonLabel,
        ariaLabelNextButton: nextMonthButtonLabel,
        ariaLabelDatesCleared: datesClearedMessage,
        ariaLabelCheckinCleared: startDateClearedMessage,
        ariaLabelCheckoutCleared: endDateClearedMessage,
        ariaLabelCheckinSelected: startDateSelectedMessage,
        ariaLabelCheckoutSelected: endDateSelectedMessage,
        ariaLabelNotValidCheckin: startDateInvalidMessage,
        ariaLabelNotValidCheckout: endDateInvalidMessage,
        ariaLabelUnavailable: dateUnavailableMessage,
        directions: directionsMessage
    };

    function handleCancel(...args) {
        setIsCancelCalled(true);

        if (!hasPartialDatesValues(latestDatesValues)) {
            setCurrentDatesValuesLatest();
        } else {
            setCurrentDatesValuesDefault();
        }

        if (shouldDatesFieldNotAutoProgressOnCancel(mediaSettings, formOptions, datesOptions)) {
            setGuestsIsOpen(false);
        }

        onCancel(...args);
    }

    function handleClose(...args) {
        if (checkDatePickerSelectDateHintExperiment(abacusStore)) {
            setDatesDayData(getDatesSelectDateHintDayData({
                dayData,
                prevDayDataId: selectedDayDataId,
                nextDayDataId: ''
            }));
        }

        setDatesIsSelecting(false);
        setDatesIsOpen(false);

        if (shouldDatesFieldAutoProgressOnClose(mediaSettings, formOptions, datesOptions, latestDatesValues) && !isCancelCalled) {
            setGuestsIsOpen(true);
        }

        if (isCancelCalled) {
            setIsCancelCalled(false);
        }

        onClose(...args);
    }

    function handleDateRangeChange(...args) {
        const [
            changedStartDate,
            changedEndDate
        ] = args;

        const changedDatesValues = {
            startDate: changedStartDate,
            endDate: changedEndDate
        };

        setLatestDatesValues(changedDatesValues);

        setShowContinueButton(hasFilledDatesValues(changedDatesValues));

        const changedDatesDayDataIds = getDatesDayDataIds(changedDatesValues);

        setDatesDayDataIds(changedDatesDayDataIds);
        if (checkDatePickerSelectDateHintExperiment(abacusStore, {shouldLogExperiment: false}, {shouldLogExperiment: !isMobile})) {
            const {
                selectedDayDataId: changedSelectedDayDataId
            } = changedDatesDayDataIds;

            setDatesDayData(getDatesSelectDateHintDayData({
                dayData,
                prevDayDataId: selectedDayDataId,
                nextDayDataId: changedSelectedDayDataId
            }));
        }

        onDateRangeChange(...args);
    }

    function handleDateRangeClear(...args) {
        setAllDatesValuesDefault();

        onDateRangeClear(...args);
    }

    /* istanbul ignore next */
    function handleDisabledDayClick(...args) {
        onDisabledDayClick(...args);
    }

    function handleFocus(...args) {
        onFocus(...args);
    }

    function handleOpen(...args) {
        setIsTypeaheadInputFocus(false);
        setDatesIsOpen(true);
        setGuestsIsOpen(false);

        setShowContinueButton(hasFilledDatesValues(latestDatesValues));

        runDatesAndNumberOfNightsExperiment({shouldLogExperiment: true}, abacusStore);
        runConsistentDayOfWeekExperiment({shouldLogExperiment: true}, abacusStore);

        if (checkDatePickerSelectDateHintExperiment(abacusStore, {shouldLogExperiment: true}, {shouldLogExperiment: isMobile})) {
            setDatesDayData(getDatesSelectDateHintDayData({
                dayData,
                prevDayDataId: selectedDayDataId,
                nextDayDataId: ''
            }));
        }

        onOpen(...args);
    }

    function handleSelectionModeChange(...args) {
        const [
            changedIsSelecting
        ] = args;

        setDatesIsSelecting(changedIsSelecting);

        onSelectionModeChange(...args);
    }

    function handleSubmit(...args) {
        setCurrentDatesValuesLatest();

        // TODO: Fix on SHCMP-430
        /* istanbul ignore next */
        if (shouldDatesFieldAutoProgressOnSubmit(mediaSettings, formOptions, datesOptions, latestDatesValues)) {
            setTimeout(function delayedGuestsOpen() {
                setGuestsIsOpen(true);
            }, 300);
        }

        onSubmit(...args);
    }

    const useHoverString = !isMobile && runDatesAndNumberOfNightsExperiment({shouldLogExperiment: false}, abacusStore) ? true : false;

    const useConsistentDayOfWeekExperiment = runConsistentDayOfWeekExperiment({shouldLogExperiment: false}, abacusStore) ? true : false;
    const shouldLogExperiment = !!startDate;
    const dateFormatOptions = runSearchFormDateFormatExperiment({shouldLogExperiment}, abacusStore) ?
        getDateFormatOptions(startDate, endDate)
        :
        propDateFormatOptions;

    const result = {
        className,
        actionLocation,
        alertTemplateComponent,
        alertTemplateData,
        calendarLabels,
        centered,
        clearButtonLabel,
        closeButtonLabel,
        consolidateInputs,
        continueButtonLabel,
        dateFormatOptions,
        dayData,
        dayTemplateComponent: SearchDayTemplate,
        directionsLabel: directionsMessage,
        disableTextInput,
        displayYearFirst,
        enableSameDayDateRange,
        endDate,
        endDateText,
        fadeOut,
        fadeOutDuration,
        i18n,
        id,
        inputIcon1,
        inputIcon2,
        inputLabel1: startDateLabel,
        inputLabel2: endDateLabel,
        inputName1,
        inputName2,
        isMobile,
        isOpen,
        locale,
        maxDate,
        minDate,
        modalTitle,
        onCancel: handleCancel,
        onClose: handleClose,
        onDateRangeChange: handleDateRangeChange,
        onDateRangeClear: handleDateRangeClear,
        onDisabledDayClick: handleDisabledDayClick,
        onFocus: handleFocus,
        onOpen: handleOpen,
        onSelectionModeChange: handleSelectionModeChange,
        onSubmit: handleSubmit,
        parentNode,
        pickerClassName,
        placement,
        positionIconRight,
        readOnly,
        scrollNode,
        showArrow,
        showContinueButton,
        splitInputs,
        startDate,
        startDateText,
        useHoverString,
        useConsistentDayOfWeekExperiment,
        wdioPrefix
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
